import { memo, useCallback, useEffect, useState, type WheelEvent } from 'react';
import cn from 'classnames';
import type { ChatMessage } from '../../model/types';
import { getChatAttachmentPreviewUrl } from '../../lib/attachmentLinks';
import { IconClose, IconPin, IconPinnedList } from '../icons';

export type ChatPinnedMessagesProps = {
  messages: ChatMessage[];
  canUnpin?: boolean;
  isPinning?: boolean;
  onUnpinMessage: (message: ChatMessage) => void | Promise<void>;
  onGoToMessage?: (messageId: number) => void;
  embedded?: boolean;
};

function getPinnedPreview(message: ChatMessage) {
  if (message.text?.trim()) {
    return message.text.trim();
  }

  if (message.attachments.length) {
    return `Attachment: ${message.attachments[0].name}`;
  }

  return 'Message';
}

function getPinnedThumbnail(message: ChatMessage): string | null {
  const image = message.attachments.find((item) => item.kind === 'image');
  if (!image) {
    return null;
  }

  return getChatAttachmentPreviewUrl(image);
}

export const ChatPinnedMessages = memo(
  ({
    messages,
    canUnpin = false,
    isPinning = false,
    onUnpinMessage,
    onGoToMessage,
    embedded = false,
  }: ChatPinnedMessagesProps) => {
    const [activeIndex, setActiveIndex] = useState(0);
    const hasMultiple = messages.length > 1;

    useEffect(() => {
      setActiveIndex((current) => {
        if (!messages.length) {
          return 0;
        }

        return Math.min(current, messages.length - 1);
      });
    }, [messages]);

    const goToNext = useCallback(() => {
      if (!hasMultiple) {
        return;
      }

      setActiveIndex((current) => (current + 1) % messages.length);
    }, [hasMultiple, messages.length]);

    const handleWheel = useCallback(
      (event: WheelEvent) => {
        if (!hasMultiple) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();

        if (event.deltaY > 0) {
          setActiveIndex((current) => (current + 1) % messages.length);
          return;
        }

        if (event.deltaY < 0) {
          setActiveIndex((current) => (current - 1 + messages.length) % messages.length);
        }
      },
      [hasMultiple, messages.length],
    );

    if (!messages.length) {
      return null;
    }

    const activeMessage = messages[activeIndex] ?? messages[0];
    const thumbnailUrl = getPinnedThumbnail(activeMessage);

    const cardClassName = embedded
      ? 'border-b border-black/[0.08] bg-white px-4 py-2.5 last:border-b-0'
      : 'rounded-[12px] border border-black/[0.08] bg-white px-3 py-2.5 shadow-[0_1px_4px_rgba(0,0,0,0.06)]';

    return (
      <div
        className={
          embedded
            ? 'flex flex-col'
            : 'relative z-20 shrink-0 border-b border-black/10 bg-content1/75 px-4 py-3 backdrop-blur-md backdrop-saturate-150'
        }
        onWheel={hasMultiple ? handleWheel : undefined}
      >
        <div className={cardClassName}>
          <div className="flex items-stretch gap-2.5">
            {hasMultiple ? (
              <div
                className="flex shrink-0 flex-col items-center justify-center gap-1 py-0.5"
                aria-label={`Pinned message ${activeIndex + 1} of ${messages.length}`}
              >
                {messages.map((message, index) => (
                  <button
                    key={`pinned-indicator-${message.id}`}
                    type="button"
                    className={cn(
                      'w-1 rounded-full transition-all',
                      index === activeIndex
                        ? 'h-5 bg-[#1E1E1E]'
                        : 'h-2 bg-black/20 hover:bg-black/35',
                    )}
                    onClick={() => setActiveIndex(index)}
                    aria-label={`Show pinned message ${index + 1}`}
                    aria-current={index === activeIndex}
                  />
                ))}
              </div>
            ) : null}

            <button
              type="button"
              className="flex min-w-0 flex-1 items-start gap-2.5 text-left"
              onClick={() => onGoToMessage?.(activeMessage.id)}
            >
              {thumbnailUrl ? (
                <img
                  src={thumbnailUrl}
                  alt=""
                  className="mt-0.5 h-10 w-10 shrink-0 rounded-[8px] object-cover"
                />
              ) : (
                <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-[8px] bg-black/[0.05] text-[#1E1E1E]">
                  <IconPin className="h-4 w-4" />
                </div>
              )}

              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <IconPin className="h-3.5 w-3.5 shrink-0 text-[#1E1E1E]" />
                  <p className="text-sm font-semibold text-[#1E1E1E]">Pinned message</p>
                  {hasMultiple ? (
                    <span className="text-[11px] font-medium text-default-400">
                      {activeIndex + 1}/{messages.length}
                    </span>
                  ) : null}
                </div>
                <p className="mt-0.5 line-clamp-1 text-sm leading-5 text-default-600">
                  {getPinnedPreview(activeMessage)}
                </p>
              </div>
            </button>

            <div className="flex shrink-0 flex-col items-center gap-1">
              {hasMultiple ? (
                <button
                  type="button"
                  className="rounded-full p-1.5 text-default-500 transition-colors hover:bg-black/[0.04] hover:text-[#1E1E1E]"
                  onClick={goToNext}
                  aria-label="Next pinned message"
                >
                  <IconPinnedList className="h-4 w-4" />
                </button>
              ) : null}
              {canUnpin ? (
                <button
                  type="button"
                  className="rounded-full p-1.5 text-default-500 transition-colors hover:bg-black/[0.04] hover:text-[#1E1E1E] disabled:opacity-50"
                  onClick={() => void onUnpinMessage(activeMessage)}
                  disabled={isPinning}
                  aria-label="Unpin message"
                >
                  <IconClose className="h-4 w-4" />
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    );
  },
);

ChatPinnedMessages.displayName = 'ChatPinnedMessages';
